'use client';

import { useState, useEffect, useCallback, RefObject } from 'react';
import { SelectionInfo } from './types';

export function useTextSelection(containerRef: RefObject<HTMLElement | null>) {
  const [selection, setSelection] = useState<SelectionInfo | null>(null);

  const handleMouseUp = useCallback((event: MouseEvent) => {
    const sel = window.getSelection();

    // If there's no text selection, check if we should preserve existing selection
    // (e.g., when clicking on toolbar buttons)
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      // Don't clear if clicking inside a toolbar or annotation panel
      const target = event.target as Element;
      const isToolbarClick = target.closest('[data-annotation-toolbar]') !== null;
      const isPanelClick = target.closest('[data-annotation-panel]') !== null;

      if (!isToolbarClick && !isPanelClick) {
        // Only clear if clicking elsewhere in the document
        const container = containerRef.current;
        if (container && container.contains(target)) {
          // Clicking inside content area without selection - clear
          setSelection(null);
        }
      }
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Check if selection is within our container
    const range = sel.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) {
      return;
    }

    // Find the block element
    let blockElement = range.startContainer.parentElement;
    while (blockElement && !blockElement.hasAttribute('data-block-index')) {
      blockElement = blockElement.parentElement;
    }

    if (!blockElement) {
      return;
    }

    const blockIndex = parseInt(blockElement.getAttribute('data-block-index') || '0', 10);
    const rect = range.getBoundingClientRect();

    // Calculate offsets relative to the block's text content
    const blockText = blockElement.textContent || '';
    const selectedText = sel.toString();
    const startOffset = blockText.indexOf(selectedText);
    const endOffset = startOffset + selectedText.length;

    setSelection({
      text: selectedText,
      blockIndex,
      startOffset: startOffset >= 0 ? startOffset : 0,
      endOffset: endOffset >= 0 ? endOffset : selectedText.length,
      rect,
    });
  }, [containerRef]);

  const clearSelection = useCallback(() => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseUp]);

  return { selection, clearSelection };
}
